import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  type DialogActionsProps as MuiDialogActionsProps,
  DialogContent as MuiDialogContent,
  type DialogContentProps as MuiDialogContentProps,
  DialogContentText as MuiDialogContentText,
  type DialogContentTextProps as MuiDialogContentTextProps,
  type DialogProps as MuiDialogProps,
  DialogTitle as MuiDialogTitle,
  type DialogTitleProps as MuiDialogTitleProps,
  IconButton,
  Slide as MuiSlide,
} from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Box, useTheme } from '@monorepo/nova-ui';

type Props = Omit<MuiDialogProps, 'open'> & {
  open?: boolean; // controlled
  defaultOpen?: boolean; // uncontrolled initial
  variant?: 'default' | 'bare';
};

export type DialogHandle = {
  open: () => void;
  close: () => void;
  isOpen: boolean;
};

// Base component
const DialogBase = forwardRef<DialogHandle, Props>(
  ({ defaultOpen, open, onClose, variant = 'default', ...muiProps }, ref) => {
    const theme = useTheme();

    const [isOpen, setIsOpen] = useState<boolean>(!!defaultOpen);

    useImperativeHandle(
      ref,
      (): DialogHandle => ({
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        isOpen, // read-only snapshot of current state
      }),
      [isOpen],
    );

    // If `open` is provided (even false), respect it; otherwise use internal state.
    const resolvedOpen = open ?? isOpen;

    const handleClose = (
      event: React.SyntheticEvent,
      reason: 'backdropClick' | 'escapeKeyDown' = 'escapeKeyDown',
    ) => {
      onClose?.(event as unknown as object, reason);
      if (open === undefined) setIsOpen(false);
    };

    return (
      <MuiDialog open={resolvedOpen} onClose={handleClose} {...muiProps}>
        {variant === 'bare' ? (
          muiProps.children
        ) : (
          <Box
            sx={{
              position: 'relative',
              padding: {
                xs: theme.spacing(6),
                md: theme.spacing(12),
              },
            }}
          >
            <IconButton
              aria-label="Close"
              onClick={(e) =>
                handleClose(
                  e as unknown as React.SyntheticEvent,
                  'escapeKeyDown',
                )
              }
              sx={{
                position: 'absolute',
                zIndex: 1,
                right: {
                  xs: theme.spacing(8),
                  md: theme.spacing(12),
                },
                top: {
                  xs: theme.spacing(8),
                  md: theme.spacing(12),
                },
                padding: 0,
              }}
            >
              <SystemIcon iconName={'X'} color={'primary'} />
            </IconButton>
            {muiProps.children}
          </Box>
        )}
      </MuiDialog>
    );
  },
);

DialogBase.displayName = 'Dialog';

// Exports matching MUI's component API
export const Dialog = DialogBase;
export const DialogTitle = MuiDialogTitle;
export const DialogActions = MuiDialogActions;
export const DialogContent = MuiDialogContent;
export const DialogContentText = MuiDialogContentText;
export const Slide = MuiSlide;

// Re-export prop types for convenience (optional)
export type {
  Props as DialogProps,
  MuiDialogTitleProps as DialogTitleProps,
  MuiDialogActionsProps as DialogActionsProps,
  MuiDialogContentProps as DialogContentProps,
  MuiDialogContentTextProps as DialogContentTextProps,
};
